"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ArticleUtils = void 0;
const AE_1 = require("./ae/AE");
const echain_product_models_1 = require("@igus/echain-product-models");
const Espool_1 = require("./espool/Espool");
const fuehrungsrinne_1 = require("./fuehrungsrinne/fuehrungsrinne");
const montage_set_1 = require("./montageSet/montage-set");
const IA_1 = require("./ia/IA");
const Kette_1 = require("./kette/Kette");
const Konfektionierung_1 = require("./konfektionierung/Konfektionierung");
const Leitung_1 = require("./leitung/Leitung");
const MontBes_1 = require("./montBes/MontBes");
const Karton_1 = require("./verpackung/Karton");
const Palette_1 = require("./verpackung/Palette");
const Trommel_1 = require("./verpackung/Trommel");
const Zubehoer_1 = require("./zubehoer/Zubehoer");
const Zugentlastung_1 = require("./zugentlastung/Zugentlastung");
const echain_shared_i18n_1 = require("@igus/echain-shared-i18n");
const Ecp_1 = require("./zubehoer/smartPlastics/ecp/Ecp");
const SmartPlastics_1 = require("./zubehoer/smartPlastics/SmartPlastics");
class ArticleUtils {
    /**
     * Retrieves the `ArticleType` of a given article
     */
    static getArticleType(article) {
        switch (article.constructor) {
            case Leitung_1.Leitung:
                return echain_product_models_1.ArticleType.leitung;
            case Kette_1.Kette:
                return echain_product_models_1.ArticleType.kette;
            case AE_1.AE:
                return echain_product_models_1.ArticleType.ae;
            case IA_1.IA:
                return echain_product_models_1.ArticleType.ia;
            case Zugentlastung_1.Zugentlastung:
                return echain_product_models_1.ArticleType.zugentlastung;
            case Palette_1.Palette:
            case Karton_1.Karton:
            case Trommel_1.Trommel:
                return echain_product_models_1.ArticleType.verpackung;
            case Konfektionierung_1.Konfektionierung:
                return echain_product_models_1.ArticleType.konfektionierung;
            case fuehrungsrinne_1.Fuehrungsrinne:
                return echain_product_models_1.ArticleType.fuehrungsrinne;
            case montage_set_1.MontageSet:
                return echain_product_models_1.ArticleType.montageSet;
            case MontBes_1.MontBes:
                return echain_product_models_1.ArticleType.montBes;
            case Espool_1.Espool:
                return echain_product_models_1.ArticleType.espool;
            case Zubehoer_1.Zubehoer:
            case Ecp_1.ECP:
            case SmartPlastics_1.SmartPlastics:
                return echain_product_models_1.ArticleType.zubehoer;
            default:
                throw new Error(`ArticleUtils.getArticleType: unknown article instance type`);
        }
    }
    /**
     * Groups articles together by using the equals method. It also maintains a count of every
     * article.
     *
     * @param articles The articles to group
     * @param options Custom grouping options.
     * @returns An array of `ArticleGroup`
     */
    static groupArticles(articles, options = {}) {
        const counter = options.counter || (() => 1);
        const comparator = ArticleUtils.getGroupArticlesComparator(options);
        const articleGroups = [];
        articles.forEach((article) => {
            const found = articleGroups.some((articleGroup) => {
                var _a;
                if (comparator(article, articleGroup.article)) {
                    articleGroup.count += counter(article);
                    (_a = articleGroup.entityIds) === null || _a === void 0 ? void 0 : _a.push(article.entityId);
                    return true;
                }
                return false;
            });
            if (!found) {
                articleGroups.push({ article, count: counter(article), entityIds: [article.entityId] });
            }
        });
        return articleGroups;
    }
    static getGroupArticlesComparator(options = {}) {
        // custom comparator always wins
        if (options.comparator)
            return options.comparator;
        const { ignoreProperties } = options;
        // standard comparator with removed properties
        if (ignoreProperties)
            return (a1, a2) => {
                const a1Clone = a1.clone();
                const a2Clone = a2.clone();
                ignoreProperties.forEach((prop) => {
                    a1Clone[prop] = undefined;
                    a2Clone[prop] = undefined;
                });
                return a1Clone.hasEqualValues(a2Clone);
            };
        // default simple hasEqualValues method
        return (a1, a2) => a1.hasEqualValues(a2);
    }
    /**
     * Retrieves the count of articles in the specific group of an article.
     *
     * @param groups The groups to search the `Article` in.
     * @param article The `Article` to get the related count for.
     * @returns The count of "equal" articles in the found `ArticleGroup` or undefined if the
     * `Article` could not be found in any group.
     */
    static getCountOfRelatedArticleGroup(groups, article) {
        for (const group of groups) {
            if (group.entityIds && group.entityIds.indexOf(article.entityId) !== -1) {
                return group.count;
            }
        }
        return undefined;
    }
    /**
     * Copies values from another `Article` into this one without affecting entityId.
     *
     * @param target The target article, the values get assigned to.
     * @param source The source article, the values get assigned from.
     */
    static assignValues(target, source) {
        if (target == null)
            throw new Error('ArticleUtils.assignValues: target is null.');
        if (source == null)
            throw new Error('ArticleUtils.assignValues: source is null.');
        if (ArticleUtils.getArticleType(target) !== ArticleUtils.getArticleType(source))
            throw new Error('ArticleUtils.assignValues: given article is from different type.');
        const json = source.toJson();
        delete json.EntityId;
        target.fromJson(json);
    }
    /**
     * Copies values from another `Article` into this one without affecting entityId.
     * In opposite to `assignValues`, it uses the overwriteUndefined Flag to strictly copy over every
     * property.
     *
     * @param target The target article, the values get assigned to.
     * @param source The source article, the values get assigned from.
     * @param preserveProperties The properties which shall not be replaced
     */
    static replaceValues(target, source, preserveProperties = []) {
        if (target == null)
            throw new Error('ArticleUtils.replaceValues: target is null.');
        if (source == null)
            throw new Error('ArticleUtils.replaceValues: source is null.');
        if (ArticleUtils.getArticleType(target) !== ArticleUtils.getArticleType(source)) {
            throw new Error('ArticleUtils.replaceValues: given article is from different type.');
        }
        // preserve _entityId to not kill that particular entity
        preserveProperties.push('EntityId');
        // clone source and remove all preserved properties
        const sourceCloneModel = source.clone().toModel();
        for (const key in sourceCloneModel) {
            if (preserveProperties.indexOf(key) !== -1) {
                delete sourceCloneModel[key];
            }
        }
        // delete all non-preserved properties from target (so that we can fill it from scratch)
        const targetModel = target.toModel();
        for (const key in targetModel) {
            if (preserveProperties.indexOf(key) === -1) {
                delete targetModel[key];
            }
        }
        // deserialize target from the non-preserved properties of source
        target.fromJson(sourceCloneModel);
    }
    /**
     * Retrieves a new empty `AllArticleIds` object.
     */
    static createEmptyAllArticleIds() {
        return this.createAllArticleTypeMap(() => new Set());
    }
    /**
     * Retrieves a new `AllArticleMap` initialized by a given factory method.
     */
    static createAllArticleTypeMap(factory) {
        return {
            [echain_product_models_1.ArticleType.ae]: factory(),
            [echain_product_models_1.ArticleType.espool]: factory(),
            [echain_product_models_1.ArticleType.fuehrungsrinne]: factory(),
            [echain_product_models_1.ArticleType.ia]: factory(),
            [echain_product_models_1.ArticleType.kette]: factory(),
            [echain_product_models_1.ArticleType.konfektionierung]: factory(),
            [echain_product_models_1.ArticleType.leitung]: factory(),
            [echain_product_models_1.ArticleType.link]: factory(),
            [echain_product_models_1.ArticleType.montageSet]: factory(),
            [echain_product_models_1.ArticleType.montBes]: factory(),
            [echain_product_models_1.ArticleType.verpackung]: factory(),
            [echain_product_models_1.ArticleType.zugentlastung]: factory(),
            [echain_product_models_1.ArticleType.zubehoer]: factory(),
        };
    }
    /**
     * Retrieves a new `AllArticleIds` object having merged multiple source objects.
     */
    static mergeAllArticleIds(...allArticleIds) {
        const mergedAllArticleIds = this.createEmptyAllArticleIds();
        allArticleIds.forEach((item) => {
            for (const key in item) {
                const articleType = key;
                item[articleType].forEach((id) => mergedAllArticleIds[articleType].add(id));
            }
        });
        return mergedAllArticleIds;
    }
    /**
     * Retrieves a new `AllArticleIds` object populated by an array of `Article`
     */
    static populateAllArticleIds(articles) {
        const allArticleIds = this.createEmptyAllArticleIds();
        articles
            .filter((article) => article.id != null)
            .forEach((article) => {
            const id = article.id;
            const articleType = this.getArticleType(article);
            return allArticleIds[articleType].add(id);
        });
        return allArticleIds;
    }
    /**
     * Retrieves a new empty `AllArticleArtNrs` object.
     */
    static createEmptyAllArticleArtNrs() {
        return {
            [echain_product_models_1.ArticleType.ae]: new Set(),
            [echain_product_models_1.ArticleType.espool]: new Set(),
            [echain_product_models_1.ArticleType.fuehrungsrinne]: new Set(),
            [echain_product_models_1.ArticleType.ia]: new Set(),
            [echain_product_models_1.ArticleType.kette]: new Set(),
            [echain_product_models_1.ArticleType.konfektionierung]: new Set(),
            [echain_product_models_1.ArticleType.leitung]: new Set(),
            [echain_product_models_1.ArticleType.link]: new Set(),
            [echain_product_models_1.ArticleType.montageSet]: new Set(),
            [echain_product_models_1.ArticleType.montBes]: new Set(),
            [echain_product_models_1.ArticleType.verpackung]: new Set(),
            [echain_product_models_1.ArticleType.zugentlastung]: new Set(),
            [echain_product_models_1.ArticleType.zubehoer]: new Set(),
        };
    }
    /**
     * Retrieves a new `AllArticleArtNrs` object having merged multiple source objects.
     */
    static mergeAllArticleArtNrs(...allArticleArtNrs) {
        const mergedAllArticleArtNrs = this.createEmptyAllArticleArtNrs();
        allArticleArtNrs.forEach((item) => {
            for (const articleType of Object.values(echain_product_models_1.ArticleType)) {
                item[articleType].forEach((localizedArtNr) => mergedAllArticleArtNrs[articleType].add(localizedArtNr));
            }
        });
        return mergedAllArticleArtNrs;
    }
    /**
     * Retrieves a new `AllArticleArtNrs` object populated by an array of `Article`
     */
    static populateAllArticleArtNrs(articles) {
        const allArticleArtNrs = this.createEmptyAllArticleArtNrs();
        articles
            .filter((article) => article.localizedArtNr != null)
            .forEach((article) => {
            return allArticleArtNrs[this.getArticleType(article)].add(JSON.stringify(article.localizedArtNr));
        });
        return allArticleArtNrs;
    }
    /**
     * Converts an `ArticleArtNrs` to `ArticleArtNrsJson`.
     */
    static articleArtNrsToJson(articleArtNrs) {
        const serializable = {};
        for (const articleType in articleArtNrs) {
            serializable[articleType] = Array.from(articleArtNrs[articleType]);
        }
        return serializable;
    }
    /**
     * Converts an `ArticleJsonDict` to `ArticleJsonDictJson`.
     */
    static articleJsonDictToJson(articleJsonDict) {
        const serializable = {};
        for (const articleType in articleJsonDict) {
            serializable[articleType] = [];
            articleJsonDict[articleType].forEach((value, key) => {
                serializable[articleType].push([key, value]);
            });
        }
        return serializable;
    }
    /**
     * Retrieves an array of dynamic class properties (which are not fetched over http).
     */
    static getDynamicClassProperties(article, articleProps) {
        return Object.keys(article.toModel()).filter((modelKey) => !articleProps.includes(modelKey));
    }
    /**
     * Retrieves the artNr for quotes. This is by definition always the `artNrGenerated` if available.
     * If not, the raw `artNr` is retrieved, optionally by a given article number country.
     *
     * @param artNrCountry The article number country.
     * @returns The artNr to be used in quotes.
     */
    static getQuoteArtNr(articleJson, artNrCountry) {
        var _a;
        if (articleJson.ArtNrGenerated)
            return articleJson.ArtNrGenerated;
        artNrCountry !== null && artNrCountry !== void 0 ? artNrCountry : (artNrCountry = echain_shared_i18n_1.LocaleService.getArtNrCountry());
        if (artNrCountry && articleJson.ArtNr && articleJson.ArtNr[artNrCountry]) {
            return articleJson.ArtNr[artNrCountry];
        }
        return (_a = articleJson.ArtNr) === null || _a === void 0 ? void 0 : _a['DE'];
    }
}
exports.ArticleUtils = ArticleUtils;
ArticleUtils.className = 'ArticleUtils';
//# sourceMappingURL=ArticleUtils.js.map